import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Inbox, Smartphone, Save, RefreshCw, BellRing, Lock, MessageSquare } from "lucide-react";
import { PushSettingsToggle, SnoozeSettings } from "@/components/PushNotifications";

const CHANNELS = [
  { key: "email", label: "Email", icon: Mail },
  { key: "inapp", label: "Cloche", icon: Inbox },
  { key: "push", label: "Push", icon: Smartphone },
  { key: "sms", label: "SMS", icon: MessageSquare },
];

const CATEGORY_LABELS = {
  paie: "Paie", documents: "Documents", planning: "Planning",
  conges: "Congés & absences", support: "Support", facturation: "Facturation",
  education: "Éducation", systeme: "Système", fiscal: "Social & fiscal",
};

export default function SalarieNotifications() {
  const [catalog, setCatalog] = useState([]);
  const [values, setValues] = useState({});
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/event-prefs");
      setCatalog(data.catalog || []);
      setValues(data.events || {});
      setCompany(data.company || {});
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const toggle = (key, ch) => {
    if ((company[key] || {})[ch] === false) return;
    setValues((v) => ({ ...v, [key]: { ...(v[key] || {}), [ch]: !(v[key] || {})[ch] } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notifications/event-prefs", { events: values });
      setValues(data.events || {});
      toast.success("Vos préférences de notification sont enregistrées");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const groups = catalog.reduce((acc, e) => {
    (acc[e.category] = acc[e.category] || []).push(e);
    return acc;
  }, {});

  return (
    <div data-testid="salarie-notifications-page">
      <PageHeader
        eyebrow="Mes notifications"
        title="Comment je veux être prévenu"
        sub="Choisissez, pour chaque type d'information, les canaux que vous acceptez. Vos réglages ne concernent que vous et ne peuvent qu'affiner ce que votre employeur a autorisé."
        right={
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} data-testid="sn-reload" className="h-10">
              <RefreshCw className="h-4 w-4 mr-1" />Recharger
            </Button>
            <Button onClick={save} disabled={saving || loading} className="h-10 bg-[#002FA7] text-white" data-testid="sn-save">
              <Save className="h-4 w-4 mr-1" />{saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-10 space-y-6 max-w-4xl">
        <div className="border border-[#DEE2E6] bg-white p-5" data-testid="sn-events-card">
          <div className="font-display font-bold text-lg flex items-center gap-2 mb-3">
            <BellRing className="h-5 w-5 text-[#002FA7]" />Par type d'information
          </div>
          {loading ? (
            <div className="space-y-2 animate-pulse" data-testid="sn-skeleton">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 bg-[#F1F3F5]" />)}
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(groups).map(([cat, list]) => (
                <div key={cat} className="border border-[#DEE2E6]" data-testid={`sn-group-${cat}`}>
                  <div className="px-3 py-2 bg-[#F8F9FA] text-[10px] uppercase tracking-[0.2em] font-bold border-b border-[#DEE2E6]">
                    {CATEGORY_LABELS[cat] || cat}
                  </div>
                  <div className="divide-y divide-[#DEE2E6]">
                    {list.map((e) => (
                      <div key={e.key} className="px-3 py-2.5 flex flex-wrap items-center gap-2">
                        <div className="flex-1 min-w-[180px] text-sm font-semibold">{e.label}</div>
                        <div className="flex gap-1.5">
                          {CHANNELS.filter((c) => e.channels.includes(c.key)).map((c) => {
                            const blocked = (company[e.key] || {})[c.key] === false;
                            const on = !blocked && (values[e.key] || {})[c.key] !== false;
                            return (
                              <button
                                key={c.key}
                                type="button"
                                disabled={blocked}
                                onClick={() => toggle(e.key, c.key)}
                                data-testid={`sn-toggle-${e.key}-${c.key}`}
                                title={blocked ? "Désactivé par votre employeur" : ""}
                                className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1 border transition ${
                                  blocked ? "bg-[#F1F3F5] text-[#ADB5BD] border-[#DEE2E6] cursor-not-allowed"
                                    : on ? "bg-[#059669] text-white border-[#059669]"
                                    : "bg-white text-[#868E96] border-[#DEE2E6]"
                                }`}
                              >
                                {blocked ? <Lock className="h-3 w-3" /> : <c.icon className="h-3 w-3" />}{c.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {catalog.length === 0 && (
                <div className="text-xs text-[#868E96]" data-testid="sn-empty">Aucun événement configurable pour votre profil.</div>
              )}
            </div>
          )}
        </div>

        <PushSettingsToggle />
        <SnoozeSettings />
      </div>
    </div>
  );
}
